/**
 * Reads the distillation table out of an action's LLM_REQUESTS rows.
 *
 * A fan-out judges one subject per call — a deal, a chunk — and that row already
 * holds everything the console needs: the identity in an `<audit record>` block
 * in the prompt (see shared/mcp_conversation_audit.py `audit_block`), the
 * content under judgement in the rest of it, and the verdict with its reason in
 * the response, whose schema the servers declare strictly. So the table is built
 * from those rows rather than from a per-record copy on the ACTION row —
 * ACTION_OUTPUT is a Snowflake VARIANT behind a hard 16MB ceiling, and one broad
 * path can propose the entire deal set.
 *
 * That makes this a CONTRACT with the two past-experience servers: the tags, the
 * identity keys and the response schemas parsed here are the ones they write. A
 * prompt or schema change that breaks it empties this table, which is why both
 * servers round-trip a built prompt through `parse_audit_block` in their tests.
 * A row this cannot read degrades to nothing — never to a thrown console.
 *
 * Kept free of React so the parsing and matching stay unit-testable in node.
 */
import { jaroWinklerSimilarity } from "../../utils/fuzzyTextMatch";
import { asRecord, parseMcpFanOut, groupMcpLlmRequestsByQuery } from "./mcpAudit";
import type { Action, LLMRequest } from "./types";

/** Which fan-out wrote the row, decided by the identity's own keys. */
export type DistillationKind = "deal" | "chunk";

export interface DistillationRecord {
  llmRequestId: string;
  kind: DistillationKind;
  /** The opaque id the row joins on: a deal id, or a RAGFlow dataset id. */
  identifier: string;
  /** What a human recognises it by: the deal name, or the document name. */
  title: string;
  /** The line under it: the portfolio company, or the deal the document is filed under. */
  subtitle: string;
  /** Doc count for a deal, chunk type for a chunk — null when the row carries neither. */
  detail: string | null;
  /** The judgement. Null when the call failed, so no verdict was ever formed. */
  verdict: boolean | null;
  reason: string | null;
  /**
   * Set when the judging call failed. Both servers fail OPEN there — the subject
   * is kept and the row stores `ERROR: …` in place of the JSON — so a row with an
   * error was included without ever being judged, which is not the same as a
   * verdict of relevant.
   */
  error: string | null;
}

export interface DistillationGroup {
  /** The path's place in the query array the caller sent, 0-based. */
  position: number;
  /** Null for rows no path claimed — see groupMcpLlmRequestsByQuery. */
  query: string | null;
  records: DistillationRecord[];
}

/**
 * The identity block a fan-out prompt carries, mirroring `audit_block`.
 *
 * Delimited by the tags, not by braces, matching the Python side: whatever sits
 * between them is handed to a JSON parser to rule on. The judged content around
 * it is free-form and liable to contain braces, so the tags are what keeps the
 * two apart — and a value holding a brace of its own, an odd document name say,
 * no longer truncates the record and leaves the row unlabelled.
 */
const AUDIT_BLOCK_PATTERN = /<audit record>\s*([\s\S]*?)\s*<\/audit record>/;

/** The prefix both servers write instead of JSON when a judging call raised. */
const ERROR_RESPONSE_PREFIX = "ERROR:";

export function parseAuditBlock(prompt: unknown): Record<string, unknown> | null {
  if (typeof prompt !== "string") return null;
  const match = AUDIT_BLOCK_PATTERN.exec(prompt);
  if (!match) return null;
  return asRecord(match[1]);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** A non-empty string, or null — so an absent reason renders as a placeholder, not "". */
function optionalText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

interface Verdict {
  verdict: boolean | null;
  reason: string | null;
  error: string | null;
}

/**
 * The judgement stored in an LLM row's response.
 *
 * Two schemas, one per server: deals-search answers with a 0/1
 * `relevant_project_conclusion_probability` and doc-q-a with a boolean
 * `relevantAndContainsFacts`. Both are strict json_schema responses, so a row
 * that parses but matches neither is a shape change, and reports itself as one
 * rather than quietly rendering as "not relevant".
 */
export function parseVerdict(response: unknown): Verdict {
  const raw = typeof response === "string" ? response.trim() : "";
  if (!raw) return { verdict: null, reason: null, error: "No response recorded" };
  if (raw.startsWith(ERROR_RESPONSE_PREFIX)) {
    return { verdict: null, reason: null, error: raw };
  }
  const record = asRecord(raw);
  if (!record) return { verdict: null, reason: null, error: "Response was not valid JSON" };
  const probability = record.relevant_project_conclusion_probability;
  if (typeof probability === "number") {
    return {
      verdict: probability === 1,
      reason: optionalText(record.relevant_project_conclusion_reason),
      error: null,
    };
  }
  const relevant = record.relevantAndContainsFacts;
  if (typeof relevant === "boolean") {
    return { verdict: relevant, reason: optionalText(record.reason), error: null };
  }
  return { verdict: null, reason: null, error: "Response matched no known verdict schema" };
}

function dealRecord(identity: Record<string, unknown>): Partial<DistillationRecord> {
  const docCount = identity.doc_count;
  return {
    kind: "deal",
    identifier: text(identity.deal_id),
    title: text(identity.deal_name),
    subtitle: text(identity.company_name),
    detail: typeof docCount === "number" ? `${docCount} doc(s)` : null,
  };
}

function chunkRecord(identity: Record<string, unknown>): Partial<DistillationRecord> {
  const chunkType = text(identity.chunk_type);
  return {
    kind: "chunk",
    identifier: text(identity.dataset_id),
    title: text(identity.document_name),
    subtitle: text(identity.deal_name),
    // Only when it is not plain text: an image chunk is judged from the picture,
    // so its prompt carries no readable content and the row must say why.
    detail: chunkType && chunkType !== "text" ? chunkType : null,
  };
}

/**
 * One LLM row as a table row, or null when the row judged no subject.
 *
 * A synthesis call, a query-expansion call and a SQL-filter call all live under
 * the same action and carry no identity block. They are not distillation rows,
 * and dropping them here is what keeps the table one-row-per-judged-subject.
 */
export function toDistillationRecord(request: LLMRequest): DistillationRecord | null {
  const identity = parseAuditBlock(request.prompt);
  if (!identity) return null;
  const subject =
    identity.deal_id !== undefined
      ? dealRecord(identity)
      : identity.chunk_id !== undefined
        ? chunkRecord(identity)
        : null;
  if (!subject) return null;
  return {
    llmRequestId: request.llm_request_id,
    ...parseVerdict(request.response),
    ...subject,
  } as DistillationRecord;
}

/**
 * The action's judged subjects, grouped under the query that caused them.
 *
 * The LLM rows carry no query, and every row of an insert batch shares one
 * CREATED_AT, so the action row's `llm_request_ids` lists are the only thing
 * that can attribute them. A group whose rows all turned out to be syntheses is
 * dropped — an empty table under a query reads as "this query found nothing",
 * which would be a lie.
 */
export function distillationGroups(
  requests: LLMRequest[],
  actionOutput: unknown,
): DistillationGroup[] {
  return groupMcpLlmRequestsByQuery(requests, parseMcpFanOut(actionOutput))
    .map((group) => ({
      position: group.position,
      query: group.query,
      records: group.requests
        .map(toDistillationRecord)
        .filter((record): record is DistillationRecord => record !== null),
    }))
    .filter((group) => group.records.length > 0);
}

/** The suffix both servers give the pipeline stage that judges and synthesises. */
const DISTILLATION_ACTION_SUFFIX = "_distillation";

/**
 * The turn's actions whose LLM rows are judgements.
 *
 * The other stages of a search — planning, the permission filter, the PC gate —
 * make no LLM calls at all, so fetching their requests to find none would be
 * network for nothing.
 */
export function distillationActions(actions: Action[]): Action[] {
  return actions.filter((action) => action.action_type.endsWith(DISTILLATION_ACTION_SUFFIX));
}

/**
 * Similarity above which a typed term is treated as naming a word it does not
 * spell exactly. Jaro-Winkler weights a shared prefix, so this comfortably
 * accepts the transpositions and dropped letters people actually type
 * ("opthalmology" for "ophthalmology") without pulling in merely similar words.
 */
const FUZZY_WORD_THRESHOLD = 0.88;

/** Terms below this length only ever match as substrings — see fuzzyMatches. */
const MIN_FUZZY_TERM_LENGTH = 4;

function words(value: string): string[] {
  return value.split(/[^a-z0-9]+/i).filter(Boolean);
}

/**
 * Whether a record's searchable text matches the filter, fuzzily.
 *
 * Every whitespace-separated term must match somewhere, so extra words narrow
 * rather than widen — that is the "traditional keyword search" half. A term
 * matches as a plain substring, or, failing that, if it is close enough to some
 * word in the text under the repo's Jaro-Winkler similarity, which is the fuzzy
 * half: an admin who half-remembers a company name or mistypes a sub-sector
 * still finds the row.
 *
 * Short terms are held to substring matching only. On three characters
 * Jaro-Winkler scores almost anything highly, so allowing it there would make a
 * short filter select the whole table.
 */
export function fuzzyMatches(term: string, haystack: string[]): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  const hay = haystack.join(" ").toLowerCase();
  const hayWords = words(hay);
  return needle.split(/\s+/).every((part) => {
    if (hay.includes(part)) return true;
    if (part.length < MIN_FUZZY_TERM_LENGTH) return false;
    return hayWords.some((word) => jaroWinklerSimilarity(part, word) >= FUZZY_WORD_THRESHOLD);
  });
}

/**
 * How a record's judgement reads in the table.
 *
 * Lives here rather than in the cell that renders it because the filter has to
 * search the same words the reader can see: two copies of this mapping would let
 * the table say "Not judged" while typing it found nothing.
 *
 * An empty string for a row with no verdict and no error — the table shows a dash
 * there, and a dash is not something anyone filters for.
 */
export function verdictLabel(record: DistillationRecord): string {
  if (record.error) return "Not judged";
  if (record.verdict === null) return "";
  return record.verdict ? "Relevant" : "Rejected";
}

/**
 * Every field of a record a filter should look at — one per column the table
 * renders, so anything on screen can be typed to find it.
 *
 * The doc count / chunk type and the request id are in here because they are
 * columns, not decoration: chasing one id across a fan-out, or pulling out every
 * image chunk, is the reason someone opens this table in the first place.
 */
export function recordSearchText(record: DistillationRecord): string[] {
  return [
    record.identifier,
    record.title,
    record.subtitle,
    record.detail ?? "",
    verdictLabel(record),
    record.reason ?? "",
    record.error ?? "",
    record.llmRequestId,
  ];
}

/**
 * Narrow groups to a query and/or a filter term, dropping groups left empty.
 *
 * A group that matched nothing is removed rather than shown empty: while
 * filtering, an empty table under a query reads as a failed search, not as
 * "this path judged nothing".
 */
export function filterDistillationGroups(
  groups: DistillationGroup[],
  term: string,
  position: number | null,
): DistillationGroup[] {
  const scoped = position === null ? groups : groups.filter((g) => g.position === position);
  if (!term.trim()) return scoped;
  return scoped
    .map((group) => ({
      ...group,
      records: group.records.filter((record) => fuzzyMatches(term, recordSearchText(record))),
    }))
    .filter((group) => group.records.length > 0);
}

/** Total records across groups — the count the "All queries" chip shows. */
export function countRecords(groups: DistillationGroup[]): number {
  return groups.reduce((total, group) => total + group.records.length, 0);
}
